import { useUserStore } from '@/store/use-user-store';
import { Icon } from '@iconify/react';
import { cn, ScrollShadow, Skeleton, Tooltip } from '@nextui-org/react';
import { memo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import useGetPractices from '../../hooks/queries/practices/useGetPractices';
import { StandardResponse } from '../../types/agent';

interface PracticeData {
  id: number;
  name: string;
  description: string;
  created_by: Record<string, string>;
  created_at: string;
}

interface ChatItemProps {
  id: number;
  title: string;
  date: string;
  relativeTime: string;
  isExpanded: boolean;
  isActive: boolean;
  tenantSlug: string;
}

const ChatItem = memo(
  ({
    id,
    title,
    date,
    relativeTime,
    isExpanded,
    isActive,
    tenantSlug,
  }: ChatItemProps) => {
    const fullLabel = `${date} - ${title}`;

    return (
      <Tooltip content={fullLabel} placement="right" isDisabled={!isExpanded}>
        <Link
          to={`/${tenantSlug}/practices/${id}/run/?action=${encodeURIComponent(title)}&id=${id}`}
          className={cn(
            'group relative flex cursor-pointer flex-col rounded-lg px-3 py-3 transition-all duration-200 ease-in-out hover:bg-[#fffff]',
            isActive
              ? 'bg-default-100 text-default-700'
              : 'hover:bg-default-50 hover:translate-x-1'
          )}
        >
          {isExpanded && (
            <div className="flex flex-col min-w-0 gap-1">
              <span className="text-sm font-medium text-foreground-700 truncate">
                {`${date} - ${title}`}
              </span>
              <span className="text-xs text-gray-400">{relativeTime}</span>
            </div>
          )}
        </Link>
      </Tooltip>
    );
  }
);

ChatItem.displayName = 'ChatItem';

interface RecentChatsProps {
  isExpanded: boolean;
}

export function RecentChats({ isExpanded }: RecentChatsProps) {
  const location = useLocation();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const userId = useUserStore((state) => state.user?.id);

  const {
    data: practicesData,
    isLoading,
    isError,
  } = useGetPractices({ has_posts: true, user_id: userId }, !!userId);

  const isActiveRoute = (path: string) => {
    // Remove query parameters from the current location pathname
    const currentPath = location.pathname.split('?')[0];
    // Remove trailing slash if present for consistent comparison
    return currentPath.replace(/\/$/, '') === path.replace(/\/$/, '');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return `${day} ${month}`;
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  // Filter and sort practices to get recent ones for the current user
  const recentPractices =
    (practicesData as StandardResponse<PracticeData>)?.results || []; // Get latest 10 practices

  if (!isExpanded) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-3 px-3">
          <Icon
            icon="lucide:message-square"
            className="w-4 h-4 text-foreground-500"
          />
          <h3 className="text-xs font-medium text-foreground-500 uppercase tracking-wider">
            Recent chats
          </h3>
        </div>
        <div className="rounded-lg p-2">
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-10 w-full rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError || recentPractices.length === 0) {
    return (
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-3 px-3">
          <Icon
            icon="lucide:message-square"
            className="w-4 h-4 text-gray-600"
          />
          <h3 className="text-xs font-normal text-gray-600 uppercase tracking-wider">
            Recent chats
          </h3>
        </div>
        <div className="rounded-lg p-2 flex items-center justify-center h-20">
          <div className="text-xs text-foreground-300 text-center">
            {isError ? 'Failed to load recent chats' : 'No recent chats'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <div className="flex items-center gap-2 mb-3 px-3">
        <Icon icon="lucide:message-square" className="w-4 h-4 text-gray-600" />
        <h3 className="text-xs font-normal text-gray-600 uppercase tracking-wider">
          Recent chats
        </h3>
      </div>
      <div className="rounded-lg p-2 px-4">
        <ScrollShadow className="max-h-[60vh]">
          <div className="flex flex-col gap-2 pb-5">
            {recentPractices.map((practice: PracticeData) => (
              <ChatItem
                key={practice.id}
                id={practice.id}
                title={practice.name}
                date={formatDate(practice.created_at)}
                relativeTime={formatRelativeTime(practice.created_at)}
                tenantSlug={tenantSlug || ''}
                isExpanded={isExpanded}
                isActive={isActiveRoute(
                  `/${tenantSlug}/practices/${practice.id}/run`
                )}
              />
            ))}
          </div>
        </ScrollShadow>
      </div>
    </div>
  );
}
